// components/VirtualWorkSpace/RealTimeWall.jsx — VERSION FINALE & CORRIGÉE

import { formatDistanceToNowStrict } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  BarChart3,
  FileText,
  Film,
  Flame,
  HelpCircle,
  Image as ImageIcon,
  Link2,
  MessageSquare,
  PartyPopper,
  Pin,
  Send,
  Star,
  Trophy,
  Users,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useRealTimeWallSocket } from '../../socket/useRealTimeWallSocket';
import { usePesaParcours } from '../../stores/usePesaParcours';


const normalizeReactions = (reactions) => {
  if (reactions instanceof Map) return reactions;
  if (!reactions || typeof reactions !== 'object') return new Map();
  return new Map(Object.entries(reactions));
};

const RealTimeWall = () => {
  const { darkMode } = useTheme();
  const { activityId } = useParams();
  const { activeItem } = usePesaParcours();
  const { user: profile } = useAuth(); 
  const {    
    wallPosts,
    wallLeaderboards,
    wallOnlineUsers = {},
    joinWall,
    leaveWall,
    sendWallPost,
    sendWallReaction,
    isConnected: isRealTimeWallConnected,
  } = useRealTimeWallSocket();

  const [message, setMessage] = useState('');
  const endRef = useRef(null);

  const activity = activeItem?.type === 'activity' ? activeItem.data : null;
  const realTime = activity?.realTime || {};

  // Posts bruts du socket
  const rawPosts = useMemo(() => {
    const key = `activity_${activityId}`;
    return (wallPosts && wallPosts[key]) || [];
  }, [wallPosts, activityId]);

  // Normalisation complète des posts (réactions → Map + memo)
  const posts = useMemo(() => {
    return rawPosts.map(post => ({
      ...post,
      reactions: normalizeReactions(post.reactions),
    }));
  }, [rawPosts]);

  const leaderboard = useMemo(() => {
    const key = `activity_${activityId}`;
    return (wallLeaderboards && wallLeaderboards[key]) || [];
  }, [wallLeaderboards, activityId]);

  const onlineUserIds = useMemo(() => {
    const key = `activity_${activityId}`;
    return new Set(wallOnlineUsers[key] || []);
  }, [wallOnlineUsers, activityId]);

  const totalParticipants = useMemo(() => {
    return new Set(posts.map(p => p.author?.id || p.author?._id)).size;
  }, [posts]);

  useEffect(() => {
    if (isRealTimeWallConnected && activityId && profile?._id) {
      joinWall({ activityId });
      return () => leaveWall({ activityId });
    }
  }, [isRealTimeWallConnected, activityId, profile?._id, joinWall, leaveWall]);

// Scroll auto fluide
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [posts]);

  const handleSend = () => {
    if (!message.trim() || !profile?._id) return;
    if (!isRealTimeWallConnected) {
      console.warn('Socket non connecté, veuillez patienter...');
      return;
    }
    const payload = { activityId, content: message.trim(), type: 'text' };
    sendWallPost(payload);
    setMessage('');
  };

  const handleReaction = (postId, emoji) => {
  if (!profile?._id) return;
  sendWallReaction({ postId, reaction: emoji });
};

  const getTypeConfig = (type) => {
    const configs = {
      text: { icon: MessageSquare, color: 'cyan', label: 'Observation' },
      image: { icon: ImageIcon, color: 'emerald', label: 'Image' },
      video: { icon: Film, color: 'purple', label: 'Vidéo' },
      pdf: { icon: FileText, color: 'rose', label: 'Document' },
      progress: { icon: BarChart3, color: 'lime', label: 'Avancement' },
      question: { icon: HelpCircle, color: 'amber', label: 'Question' },
      celebration: { icon: PartyPopper, color: 'pink', label: 'Célébration' },
      resource: { icon: Link2, color: 'indigo', label: 'Ressource' },
    };
    return configs[type] || configs.text;
  };

  const reactionEmojis = ['👍', '❤️', '🔥', '⚡'];

  return (
    <div className={`min-h-screen flex flex-col ${darkMode ? 'bg-[#020617]' : 'bg-slate-50'}`}>
      {/* HEADER */}
      <header className={`border-b ${darkMode ? 'border-cyan-500/10 bg-black/40' : 'border-slate-200 bg-white/70'} backdrop-blur-xl px-6 py-6`}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex-1">
              <h1 className="text-2xl font-black uppercase tracking-tight text-white flex items-center gap-3">
                <div className="w-2 h-8 bg-cyan-500 rounded-full" />
                {realTime.wallTitle || 'Mur collaboratif'}
              </h1>
              <p className="text-slate-400 mt-2 text-sm leading-relaxed">
                {realTime.wallDescription}
              </p>
            </div>

            {/* BADGES – RESPONSIVE (empilés sur petit écran) */}
            <div className="flex flex-wrap gap-4 justify-start md:justify-end">
              {/* Communauté */}
              <div className={`px-4 py-2 rounded-lg border min-w-[160px] ${darkMode ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200'}`}>
                <span className="block text-[10px] uppercase tracking-widest text-slate-500 font-bold">Communauté</span>
                <span className="text-cyan-400 font-mono font-bold flex items-center gap-2 mt-1">
                  <Users size={16} />
                  {totalParticipants} participant{totalParticipants > 1 ? 's' : ''}
                  {onlineUserIds.size > 0 && (
                    <span className="text-green-400 text-xs">• {onlineUserIds.size} en ligne</span>
                  )}
                </span>
              </div>

              {/* Streak */}
              {realTime.streakEligible && profile?.currentStreak > 0 && (
                <div className="px-4 py-2 rounded-lg border border-orange-500/30 bg-orange-500/10 min-w-[160px]">
                  <span className="block text-[10px] uppercase text-orange-400 font-bold">Streak</span>
                  <span className="text-orange-300 font-mono font-bold flex items-center gap-2">
                    <Flame size={16} /> {profile.currentStreak} jour{profile.currentStreak > 1 ? 's' : ''}
                  </span>
                </div>
              )}

              {/* Flash Challenge – limité en largeur */}
              {realTime.flashChallenge?.active && (
                <div className="px-4 py-3 rounded-lg border border-orange-500/40 bg-gradient-to-br from-orange-500/10 to-pink-500/10 animate-pulse max-w-[280px]">
                  <span className="block text-[10px] uppercase tracking-widest text-orange-400 font-bold">Défi Flash</span>
                  <p className="text-sm font-medium text-orange-200 mt-1 leading-tight">
                    {realTime.flashChallenge.title}
                  </p>
                  <span className="text-xs text-orange-300 flex items-center gap-2 mt-2">
                    <Trophy size={15} /> +{realTime.flashChallenge.bonusPoints} points
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Tags Mentor */}
          {realTime.instantFeedback?.enabled && (
            <div className="flex flex-wrap items-center gap-3 mt-6 pt-5 border-t border-white/5">
              <span className="text-[10px] uppercase font-black text-slate-500 tracking-widest">Tags mentor :</span>
              <div className="flex gap-2 flex-wrap">
                {realTime.instantFeedback.mentorTags.map(tag => (
                  <span key={tag} className="text-xs font-mono px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/30">
                    {tag}
                  </span>
                ))}
              </div>
              <span className="text-[10px] text-slate-500 ml-auto italic">
                Retour sous {realTime.instantFeedback.maxDelayHours}h
              </span>
            </div>
          )}
        </div>
      </header>

      {/* CONTENU */}
      <div className="flex-1 flex max-w-7xl mx-auto w-full px-6 py-8 gap-8">
        {/* COLONNE POSTS */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex-1 overflow-y-auto no-scrollbar space-y-6 pb-40">
            {/* Welcome pinned */}
            {realTime.welcomeMessage?.isPinned && (
              <div className="p-6 rounded-2xl border border-cyan-500/40 bg-cyan-500/5 backdrop-blur-sm relative">
                <Pin className="absolute top-4 right-4 text-cyan-400 opacity-60" size={20} />
                <h4 className="text-xs font-black uppercase tracking-[0.2em] text-cyan-400 mb-3 flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-ping" />
                  Message d'ouverture
                </h4>
                <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-line font-medium italic">
                  {realTime.welcomeMessage.content}
                </p>
              </div>
            )}

            {/* Posts */}
            {posts.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center opacity-50">
                <MessageSquare size={80} className="mb-6 text-cyan-500/50" />
                <p className="text-xl font-mono uppercase tracking-wider text-cyan-400/70">
                  Aucune contribution pour l'instant
                </p>
                <p className="text-sm text-slate-500 mt-2">Soyez le premier à partager</p>
              </div>
            ) : (
              posts.map((post) => {
                const typeConfig = getTypeConfig(post.type);
                const Icon = typeConfig.icon;
                const isOnline = onlineUserIds.has(post.author?.id || post.author?._id);

                return (
                  <div
                    key={post._id}
                    className={`p-6 rounded-2xl border relative group
                      ${post.isPinned || post.isFeatured
                        ? 'border-yellow-500/50 bg-yellow-500/5'
                        : darkMode
                        ? 'bg-white/5 border-white/10'
                        : 'bg-white border-slate-200'
                      } backdrop-blur-sm hover:border-${typeConfig.color}-500/40 transition-all`}
                  >
                    {(post.isPinned || post.isFeatured) && (
                      <div className="absolute top-3 right-3 flex gap-1">
                        {post.isPinned && <Pin size={16} className="text-yellow-400" />}
                        {post.isFeatured && <Star size={16} className="text-yellow-400" />}
                      </div>
                    )}

                    <div className="flex items-start gap-4">
                      <div className="relative flex-shrink-0">
                        {post.author?.avatar ? (
                          <img
                            src={post.author.avatar}
                            alt={post.author.name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-cyan-500/30"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-cyan-500/20 border-2 border-cyan-500/30 flex items-center justify-center text-cyan-300 font-black text-lg">
                            {(post.author?.name || '?')[0].toUpperCase()}
                          </div>
                        )}
                        {isOnline && (
                          <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-[#020617]" />
                        )}
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-bold text-lg text-white">{post.author?.name || 'Anonyme'}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full bg-${typeConfig.color}-500/20 text-${typeConfig.color}-400 border border-${typeConfig.color}-500/30 flex items-center gap-1`}>
                            <Icon size={12} />
                            {typeConfig.label}
                          </span>
                          {post.points > 0 && (
                            <span className="text-xs text-cyan-400 font-mono">+{post.points} pts</span>
                          )}
                        </div>

                        <div className="text-xs opacity-60 font-mono text-cyan-400 mb-3">
                          {formatDistanceToNowStrict(new Date(post.createdAt), { addSuffix: true, locale: fr })}
                        </div>

                        <p className={`text-base leading-relaxed whitespace-pre-line ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                          {post.content}
                        </p>

                        {/* Média */}
                        {post.mediaUrl && (
                          <div className="mt-4 rounded-xl overflow-hidden border border-white/10">
                            <img
                              src={post.mediaPreview || post.mediaUrl}
                              alt="Média partagé"
                              className="w-full max-h-96 object-contain bg-black/50"
                            />
                          </div>
                        )}

                        {/* Réactions */}
                        <div className="flex items-center gap-4 mt-5">
                          {reactionEmojis.map((emoji) => {
                            const count = post.reactions.get(emoji) || 0;
                            return (
                              <button
                                key={emoji}
                                onClick={() => handleReaction(post._id, emoji)}
                                className="flex items-center gap-1 text-lg hover:scale-110 transition-transform"
                              >
                                <span>{emoji}</span>
                                {count > 0 && <span className="text-xs text-slate-400">{count}</span>}
                              </button>
                            );
                          })}
                          <span className="text-xs text-slate-500 ml-auto">
                            {post.comments?.length || 0} commentaire{post.comments?.length > 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={endRef} />
          </div>

          {/* INPUT FIXE */}
          <div className={`fixed bottom-0 left-0 right-0 border-t ${darkMode ? 'border-cyan-500/10 bg-[#020617]/95' : 'border-slate-200 bg-white/95'} backdrop-blur-xl z-50`}>
            <div className="max-w-7xl mx-auto px-8 py-6">
              <div className="flex gap-4">
                <textarea
                  disabled={!isRealTimeWallConnected}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSend())}
                  placeholder="Partagez une observation, une question, une hypothèse ou un progrès..."
                  className={`flex-1 px-6 py-4 rounded-xl border ${darkMode
                    ? 'bg-white/5 border-white/10 text-white placeholder-slate-500 focus:border-cyan-500'
                    : 'bg-slate-50 border-slate-300 text-slate-900 placeholder-slate-500 focus:border-cyan-600'
                  } focus:outline-none resize-none transition-all`}
                  rows="2"
                />
                <button
                  onClick={handleSend}
                  disabled={!message.trim() || !isRealTimeWallConnected}
                  className="px-8 py-4 bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-600 disabled:opacity-50 text-white font-black uppercase tracking-wider rounded-xl transition-all flex items-center gap-3 h-fit self-end"
                >
                  Publier <Send size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* LEADERBOARD */}
        {realTime.leaderboardEnabled && leaderboard.length > 0 && (
          <div className="w-80 hidden xl:block flex-shrink-0">
            <div className={`h-fit rounded-2xl border ${darkMode ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200'} p-6 sticky top-8`}>
              <h3 className="text-sm font-black uppercase tracking-[0.2em] mb-6 flex items-center gap-3 text-white">
                <Trophy size={20} className="text-amber-400" />
                Classement
              </h3>
              <div className="space-y-4">
                {leaderboard.slice(0, 10).map((entry, i) => (
                  <div key={entry.userId || entry._id} className="flex items-center justify-between group">
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-mono w-6 text-right ${i < 3 ? 'text-yellow-400 font-black' : 'text-slate-500'}`}>
                        {i + 1}
                      </span>
                      <div className="flex items-center gap-2">
                        {entry.avatar ? (
                          <img src={entry.avatar} alt="" className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs text-white">
                            {(entry.name || entry.nom || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <span className="text-sm font-medium text-slate-300 group-hover:text-white transition-colors">
                          {entry.name || entry.nom || 'Anonyme'}
                        </span>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-cyan-400 bg-cyan-400/10 px-3 py-1 rounded-full">
                      {entry.count} contribution{entry.count > 1 ? 's' : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RealTimeWall;